import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { getStoreProducts, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Motion";

export default function StoreProduct() {
  const { lookupKey } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState(null);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getStoreProducts()
      .then((list) => {
        const p = list.find((x) => x.lookupKey === lookupKey);
        setProduct(p || false);
        if (p) document.title = `${p.name} — Motion & Meaning`;
      })
      .catch(() => setProduct(false));
  }, [lookupKey]);

  if (product === false)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6">
        <p className="font-serif text-4xl">This object does not exist.</p>
        <Link to="/store" className="font-mono text-[11px] tracking-[0.2em] text-gold">← BACK TO STORE</Link>
      </div>
    );
  if (!product) return <div className="min-h-screen" />;

  return (
    <div data-testid="store-product-page">
      <section ref={ref} className="relative h-[70svh] overflow-hidden">
        <motion.img style={{ y, scale: 1.1 }} src={product.artwork} alt={product.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
      </section>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-7">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.3em] text-gold mb-4">
                {product.subtitle?.toUpperCase()} · {product.format}
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl font-light leading-[0.95] text-white break-words">{product.name}</h1>
              <p className="font-serif text-2xl md:text-3xl font-light text-white/80 mt-10 leading-snug max-w-2xl">
                {product.story}
              </p>
              <p className="text-white/60 leading-relaxed mt-8 max-w-2xl">{product.description}</p>
            </Reveal>
          </div>

          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <div className="border border-white/10 p-8 md:p-10">
                <p className="font-serif text-4xl text-white mb-8">
                  {money(product.price + (variant?.priceAdjustment || 0), product.currency)}
                </p>

                <div className="space-y-4 font-mono text-[11px] tracking-[0.15em] mb-8">
                  {product.edition && (
                    <div className="flex justify-between"><span className="text-white/40">EDITION</span><span className="text-white/80">{product.edition.toUpperCase()}</span></div>
                  )}
                  {product.dimension && (
                    <div className="flex justify-between"><span className="text-white/40">DIMENSION</span><span className="text-white/80">{product.dimension}</span></div>
                  )}
                  {product.limitedQuantity && (
                    <div className="flex justify-between"><span className="text-white/40">RUN OF</span><span className="text-gold">{product.limitedQuantity}</span></div>
                  )}
                  {product.preorder && (
                    <div className="flex justify-between"><span className="text-white/40">STATUS</span><span className="text-white/80">PRE-ORDER</span></div>
                  )}
                </div>

                {(product.variants || []).length > 0 && (
                  <div className="mb-6" data-testid="product-variants">
                    <p className="font-mono text-[9px] tracking-[0.25em] text-white/40 mb-3">SELECT OPTION</p>
                    <div className="flex flex-wrap gap-2">
                      {product.variants.map((v) => (
                        <button key={v.id} onClick={() => setVariant(v)} disabled={!v.available}
                          data-testid={`variant-opt-${v.id}`}
                          className={`font-mono text-[10px] tracking-[0.15em] border px-4 py-2.5 transition-colors ${
                            variant?.id === v.id ? "border-gold text-gold bg-gold/10" : "border-white/20 text-white/60 hover:border-white/50"
                          } ${!v.available ? "opacity-40 line-through" : ""}`}>
                          {v.label}
                          {v.priceAdjustment ? ` (${v.priceAdjustment > 0 ? "+" : "−"}${money(Math.abs(v.priceAdjustment), product.currency)})` : ""}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center border border-white/20">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-3 text-white/60 hover:text-gold" data-testid="product-qty-dec">−</button>
                    <span className="px-4 font-mono text-sm text-white" data-testid="product-qty">{qty}</span>
                    <button onClick={() => setQty((q) => q + 1)} className="px-4 py-3 text-white/60 hover:text-gold" data-testid="product-qty-inc">+</button>
                  </div>
                </div>

                <button
                  onClick={() => add(product, qty, variant)}
                  disabled={(product.variants || []).length > 0 && !variant}
                  data-testid="product-add-to-cart"
                  className="w-full bg-gold text-black font-mono text-[11px] tracking-[0.25em] py-4 hover:bg-white transition-colors disabled:opacity-40"
                >
                  {(product.variants || []).length > 0 && !variant ? "SELECT AN OPTION" : "ADD TO CART"}
                </button>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-20">
          <Link to="/store" data-testid="back-to-store" className="font-mono text-[11px] tracking-[0.2em] text-white/50 hover:text-gold transition-colors">
            ← BACK TO STORE
          </Link>
        </div>
      </div>
    </div>
  );
}
